import { Injectable } from "@angular/core";
import { HttpParams } from "@angular/common/http";
import { ApiService } from "../api.service";
import { Urls } from "../../shared/urls";
import { AssigneeEntry, AssigneeOptions, TaskCategoryWorkflowOption } from "../../interfaces/review-workflow.interface";
import { assigneeUserId } from "./assignee-display";
import {
  AssignedStepsView,
  AssignedToMeQuery,
  AssignedWorkflowView,
  AssignmentForm,
  InstanceDetailsView,
  NotifyOnCompleteView,
  StepActionView,
  StepAssigneeSelection,
  StepAssigneesView
} from "../../interfaces/review-workflow-instance.interface";

@Injectable({ providedIn: "root" })
export class ReviewWorkflowInstanceService {
  constructor(private readonly apiService: ApiService) {}

  async getForTaskCategory(documentId: string): Promise<TaskCategoryWorkflowOption | null> {
    const params = new HttpParams().set("documentId", documentId);
    const response = await this.apiService.get(Urls.reviewWorkflowForTaskCategoryPath, params);
    return response?.data ?? null;
  }

  /** The pre-creation counterpart of `previewAssignment`, which needs a task that does not exist yet. */
  async getAssignmentFormForCustomer(workflowId: string, customerId: string): Promise<AssignmentForm | null> {
    const params = new HttpParams().set("customerId", customerId);
    const response = await this.apiService.get(Urls.reviewWorkflowAssignmentFormPath(workflowId), params);
    return response?.data ?? null;
  }

  async getAssigneeOptionsForTask(transcriptId: string): Promise<AssigneeOptions> {
    const response = await this.apiService.get(Urls.reviewWorkflowAssigneeOptionsForTaskPath(transcriptId));
    return response?.data ?? { special: [], users: [], labels: [] };
  }

  async getByTranscript(transcriptId: string): Promise<InstanceDetailsView | null> {
    try {
      const response = await this.apiService.get(Urls.reviewWorkflowInstanceByTranscriptPath(transcriptId));
      return response?.data ?? null;
    } catch (error) {
      if (error?.status === 404) return null;
      throw error;
    }
  }

  async deleteByTranscript(transcriptId: string): Promise<void> {
    await this.apiService.delete(Urls.reviewWorkflowInstanceByTranscriptPath(transcriptId));
  }

  async assignTo(transcriptId: string, workflowId: string, stepAssignees?: StepAssigneeSelection[]): Promise<AssignedWorkflowView | null> {
    const response = await this.apiService.post(Urls.reviewWorkflowInstanceAssignToPath(transcriptId), { workflowId, stepAssignees });
    return response?.data ?? null;
  }

  async previewAssignment(transcriptId: string, workflowId: string): Promise<AssignmentForm | null> {
    const params = new HttpParams().set("workflowId", workflowId);
    const response = await this.apiService.get(Urls.reviewWorkflowInstanceAssignmentPreviewPath(transcriptId), params);
    return response?.data ?? null;
  }

  async updateStepAssignees(instanceId: string, stepId: string, userIds: string[]): Promise<StepAssigneesView | null> {
    const response = await this.apiService.put(Urls.reviewWorkflowInstanceStepAssigneesPath(instanceId, stepId), { userIds });
    return response?.data ?? null;
  }

  async updateNotifyOnComplete(instanceId: string, assignees: AssigneeEntry[]): Promise<NotifyOnCompleteView | null> {
    const response = await this.apiService.put(Urls.reviewWorkflowInstanceNotifyOnCompletePath(instanceId), {
      enabled: assignees.length > 0,
      assignees: assignees.map(entry => ({
        type: entry.type,
        user: assigneeUserId(entry),
        label: entry.label ?? null,
        special: entry.special ?? null
      }))
    });
    return response?.data ?? null;
  }

  async startStep(instanceId: string, stepId: string, comment?: string): Promise<StepActionView | null> {
    const response = await this.apiService.post(
      Urls.reviewWorkflowInstanceStepActionPath(instanceId, stepId, "start"),
      comment !== undefined ? { comment } : {}
    );
    return response?.data ?? null;
  }

  async approveStep(instanceId: string, stepId: string, comment?: string): Promise<StepActionView | null> {
    const response = await this.apiService.post(
      Urls.reviewWorkflowInstanceStepActionPath(instanceId, stepId, "approve"),
      comment !== undefined ? { comment } : {}
    );
    return response?.data ?? null;
  }

  async rejectStep(instanceId: string, stepId: string, comment: string): Promise<StepActionView | null> {
    const response = await this.apiService.post(Urls.reviewWorkflowInstanceStepActionPath(instanceId, stepId, "reject"), { comment });
    return response?.data ?? null;
  }

  async resolveStep(instanceId: string, stepId: string, comment: string): Promise<StepActionView | null> {
    const response = await this.apiService.post(Urls.reviewWorkflowInstanceStepActionPath(instanceId, stepId, "resolve"), { comment });
    return response?.data ?? null;
  }

  async retrySigningRequest(instanceId: string, stepId: string): Promise<void> {
    await this.apiService.post(Urls.reviewWorkflowInstanceSigningRetryPath(instanceId, stepId));
  }

  async getAssignedToMe(query?: AssignedToMeQuery): Promise<AssignedStepsView> {
    let params = new HttpParams();
    if (query?.search) params = params.set("search", query.search);
    if (query?.sortBy) params = params.set("sortBy", query.sortBy);
    if (query?.sortOrder) params = params.set("sortOrder", query.sortOrder);
    if (query?.limit) params = params.set("limit", String(query.limit));
    if (query?.skip) params = params.set("skip", String(query.skip));
    if (query?.includeMonitoring) params = params.set("includeMonitoring", "true");

    const response = await this.apiService.get(Urls.reviewWorkflowInstanceAssignedToMePath, params);
    return response?.data ?? { rows: [], total: 0, count: 0 };
  }
}
